import React, { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Edit, Trash2, Package, Tag, Layers, Calendar,
  Truck, Shield, FileText, DollarSign, CheckCircle, XCircle,
  Image as ImageIcon, Star, TrendingUp, Box, Barcode, Globe,
  Award, Clock,
} from 'lucide-react';
import { toast } from 'react-toastify';
import Layout from '../../components/Layout';
import {
  useGetProductQuery,
  useDeleteProductMutation,
} from '../../app/api';

const ProductView = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  // ✅ RTK Query — auto cached
  const { data, isLoading } = useGetProductQuery(id);
  const [deleteProduct, { isLoading: deleting }] = useDeleteProductMutation();

  const product = data?.data?.product;
  const imageBaseUrl = data?.data?.imageBaseUrl || '';
  const [activeImage, setActiveImage] = useState('');

  // ✅ Set active image when product loads
  React.useEffect(() => {
    if (product?.image) setActiveImage(product.image);
  }, [product]);

  const getFullImage = (path) => {
    if (!path) return '';
    if (path.startsWith('http')) return path;
    return `${imageBaseUrl}${path}`;
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;
    try {
      await deleteProduct(id).unwrap();
      toast.success('Product deleted successfully');
      navigate('/products');
    } catch (error) {
      toast.error(error?.data?.message || 'Failed to delete');
    }
  };

  if (isLoading) {
    return (
      <Layout>
        <div className="flex-center" style={{ height: '60vh' }}>
          <div className="spinner" />
        </div>
      </Layout>
    );
  }

  if (!product) {
    return (
      <Layout>
        <div className="flex-center" style={{ height: '60vh' }}>
          <div>Product not found</div>
        </div>
      </Layout>
    );
  }

  // Computed
  const status =
    product.stock === 0 ? 'Out of Stock'
    : product.stock <= (product.lowStockAlert || 5) ? 'Low Stock'
    : product.isActive ? 'Active'
    : 'Inactive';

  const statusClass =
    status === 'Active' ? 'badge-success'
    : status === 'Low Stock' ? 'badge-warning'
    : 'badge-danger';

  const discountPercent =
    product.mrp > 0 && product.price < product.mrp
      ? Math.round(((product.mrp - product.price) / product.mrp) * 100)
      : 0;

  const allImages = [product.image, ...(product.gallery || [])].filter(Boolean);

  return (
    <Layout>
      <div className="view-product-page">
        {/* HEADER */}
        <div className="page-header">
          <div>
            <Link
              to="/products"
              className="btn btn-sm btn-outline btn-back"
              style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', marginBottom: '8px' }}
            >
              <ArrowLeft size={16} />
              <span>Back to Products</span>
            </Link>
            <h1 className="page-title">{product.name}</h1>
            <div className="page-subtitle-row">
              <span className={`badge ${statusClass}`}>{status}</span>
              {product.category?.name && <span className="badge badge-info">{product.category.name}</span>}
              {product.isFeatured && (
                <span className="badge badge-primary"><Star size={12} /> Featured</span>
              )}
              {product.isTrending && (
                <span className="badge badge-warning"><TrendingUp size={12} /> Trending</span>
              )}
            </div>
          </div>
          <div className="page-actions">
            <button className="btn btn-danger" onClick={handleDelete} disabled={deleting}>
              <Trash2 size={18} />
              {deleting ? 'Deleting...' : 'Delete'}
            </button>
            <Link to={`/products/edit/${product._id}`} className="btn btn-primary">
              <Edit size={18} /> Edit Product
            </Link>
          </div>
        </div>

        {/* IMAGES + INFO */}
        <div className="view-product-grid">
          <div className="card product-images-card">
            <div className="product-main-image">
              {activeImage ? (
                <img src={getFullImage(activeImage)} alt={product.name} className="main-image" />
              ) : (
                <div className="image-placeholder">
                  <ImageIcon size={64} />
                  <span>No Image</span>
                </div>
              )}
              {discountPercent > 0 && (
                <div className="discount-badge">-{discountPercent}%</div>
              )}
            </div>
            {allImages.length > 1 && (
              <div className="product-thumbnails">
                {allImages.map((img, index) => (
                  <button
                    key={index}
                    className={`thumbnail-btn ${activeImage === img ? 'active' : ''}`}
                    onClick={() => setActiveImage(img)}
                  >
                    <img src={getFullImage(img)} alt={`Thumb ${index + 1}`} />
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="card">
            <div className="product-header-info">
              <div className="product-pricing">
                <span className="current-price">₹{product.price?.toLocaleString()}</span>
                {product.mrp > 0 && product.mrp > product.price && (
                  <>
                    <span className="original-price">₹{product.mrp?.toLocaleString()}</span>
                    <span className="discount-text">{discountPercent}% OFF</span>
                  </>
                )}
              </div>
              {product.brand && (
                <div className="product-brand">
                  <span className="label">Brand:</span>
                  <span className="value">{product.brand}</span>
                </div>
              )}
            </div>

            <div className="info-grid">
              <div className="info-item">
                <Barcode size={16} />
                <div>
                  <span className="info-label">SKU</span>
                  <span className="info-value">{product.sku || 'N/A'}</span>
                </div>
              </div>
              <div className="info-item">
                <Box size={16} />
                <div>
                  <span className="info-label">Stock</span>
                  <span className={`info-value ${product.stock === 0 ? 'text-danger' : ''}`}>
                    {product.stock} units
                  </span>
                </div>
              </div>
              <div className="info-item">
                <Star size={16} />
                <div>
                  <span className="info-label">Rating</span>
                  <span className="info-value">
                    {product.rating > 0 ? `${product.rating} ⭐` : 'No rating'}
                  </span>
                </div>
              </div>
              <div className="info-item">
                <Calendar size={16} />
                <div>
                  <span className="info-label">Created</span>
                  <span className="info-value">
                    {new Date(product.createdAt).toLocaleDateString('en-IN', {
                      day: '2-digit', month: 'short', year: 'numeric',
                    })}
                  </span>
                </div>
              </div>
            </div>

            {product.shortDescription && (
              <div className="short-desc">{product.shortDescription}</div>
            )}
          </div>
        </div>

        {/* DESCRIPTION */}
        <div className="card">
          <h3 className="card-title"><FileText size={18} /> Description</h3>
          <p className="description-text">{product.description || 'No description'}</p>
        </div>

        {/* PRICING DETAILS */}
        <div className="card">
          <h3 className="card-title"><DollarSign size={18} /> Pricing</h3>
          <div className="details-grid">
            <div className="detail-pair"><span className="detail-label">Price</span><span className="detail-value">₹{product.price}</span></div>
            <div className="detail-pair"><span className="detail-label">MRP</span><span className="detail-value">₹{product.mrp || 0}</span></div>
            <div className="detail-pair"><span className="detail-label">Cost</span><span className="detail-value">₹{product.costPrice || 0}</span></div>
            <div className="detail-pair"><span className="detail-label">Tax</span><span className="detail-value">{product.tax || 0}%</span></div>
          </div>
        </div>

        {/* VARIANTS */}
        {product.variants?.length > 0 && (
          <div className="card">
            <h3 className="card-title"><Layers size={18} /> Variants</h3>
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr><th>Name</th><th>Value</th><th>Price</th><th>Stock</th><th>SKU</th></tr>
                </thead>
                <tbody>
                  {product.variants.map((v, i) => (
                    <tr key={i}>
                      <td>{v.variantName}</td>
                      <td>{v.variantValue}</td>
                      <td>₹{v.price}</td>
                      <td>{v.stock}</td>
                      <td>{v.sku || 'N/A'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* SHIPPING */}
        <div className="card">
          <h3 className="card-title"><Truck size={18} /> Shipping</h3>
          <div className="details-grid">
            <div className="detail-pair"><span className="detail-label">Weight</span><span className="detail-value">{product.weight || 0} g</span></div>
            <div className="detail-pair"><span className="detail-label">Shipping Charge</span><span className="detail-value">₹{product.shippingCharge || 0}</span></div>
            <div className="detail-pair"><span className="detail-label">Free Shipping</span><span className="detail-value">{product.freeShipping ? <CheckCircle size={14} className="text-success" /> : <XCircle size={14} className="text-danger" />}</span></div>
            <div className="detail-pair"><span className="detail-label">Delivery Time</span><span className="detail-value">{product.deliveryTime || 'N/A'}</span></div>
          </div>
        </div>

        {/* FLAGS */}
        <div className="card">
          <h3 className="card-title">Status & Flags</h3>
          <div className="flags-grid">
            {[
              { key: 'isActive', label: 'Active' },
              { key: 'isFeatured', label: 'Featured' },
              { key: 'isTrending', label: 'Trending' },
              { key: 'isNewArrival', label: 'New Arrival' },
              { key: 'isBestSeller', label: 'Best Seller' },
              { key: 'isRecommended', label: 'Recommended' },
              { key: 'approvedByAdmin', label: 'Approved by Admin' },
            ].map((flag) => (
              <div key={flag.key} className="flag-item">
                {product[flag.key] ? (
                  <CheckCircle size={16} className="text-success" />
                ) : (
                  <XCircle size={16} className="text-muted" />
                )}
                <span>{flag.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default ProductView;